import React, { useMemo, useState } from "react";
import {
  Button,
  Collapse,
  ConfigProvider,
  Empty,
  List,
  Modal,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { getBytes, ref } from "firebase/storage";
import { buildFreightDocumentsByStoppingPoint } from "../../../util/freight/freight-document-utils";
import { storage } from "../../../config/firebase";
import theme from "../../../theme";

const { Text } = Typography;

const antdModalTheme = {
  token: {
    colorPrimary: theme.colors.primary,
    colorText: theme.colors.textPrimary,
    colorTextSecondary: theme.colors.textSecondary,
    colorBorder: theme.colors.primaryBorder,
    borderRadius: theme.radius.small,
    fontFamily: theme.typography.fontFamily,
  },
};

const MIME_EXTENSIONS = {
  "application/pdf": ".pdf",
  "image/jpeg": ".jpg",
  "image/jpg": ".jpg",
  "image/png": ".png",
  "image/gif": ".gif",
  "image/webp": ".webp",
  "image/bmp": ".bmp",
  "application/xml": ".xml",
  "text/xml": ".xml",
  "text/plain": ".txt",
};

function sanitizeFileName(value) {
  const sanitized = String(value || "documento")
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_")
    .replace(/\s+/g, " ")
    .trim();

  return sanitized.slice(0, 180) || "documento";
}

function extractFileNameFromUrl(url) {
  const raw = String(url || "")
    .split("?")[0]
    .split("/")
    .pop();

  if (!raw) return "";

  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function getExtensionFromValue(value) {
  const match = String(value || "").match(/(\.[a-z0-9]{2,8})$/i);
  return match ? match[1].toLowerCase() : "";
}

function getExtensionFromMimeType(mimeType) {
  return MIME_EXTENSIONS[String(mimeType || "").toLowerCase()] || "";
}

function resolveFileName(documentItem, mimeType) {
  const fromUrl = extractFileNameFromUrl(documentItem?.url);
  if (fromUrl && getExtensionFromValue(fromUrl)) {
    return sanitizeFileName(fromUrl);
  }

  const preferredBase = sanitizeFileName(
    documentItem?.label || documentItem?.name || fromUrl || "documento",
  );

  const extension =
    getExtensionFromValue(preferredBase) ||
    getExtensionFromValue(fromUrl) ||
    getExtensionFromMimeType(mimeType) ||
    getExtensionFromMimeType(documentItem?.contentType) ||
    "";

  if (getExtensionFromValue(preferredBase)) {
    return preferredBase;
  }

  return `${preferredBase}${extension || ".bin"}`;
}

function getStoragePathFromDownloadUrl(downloadUrl = "") {
  const value = String(downloadUrl || "");
  const marker = "/o/";
  const start = value.indexOf(marker);
  if (start === -1) return "";

  const pathStart = start + marker.length;
  const pathEnd = value.indexOf("?", pathStart);
  const encodedPath =
    pathEnd === -1
      ? value.substring(pathStart)
      : value.substring(pathStart, pathEnd);

  try {
    return decodeURIComponent(encodedPath);
  } catch {
    return encodedPath;
  }
}

function openDocument(url) {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

function triggerBlobDownload(blob, fileName) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  link.rel = "noopener";
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);

  // Alguns navegadores cancelam o download se a URL for revogada imediatamente.
  window.setTimeout(() => {
    window.URL.revokeObjectURL(objectUrl);
  }, 1500);
}

async function fetchBlobFromFirebaseStorage(url) {
  const storagePath = getStoragePathFromDownloadUrl(url);
  if (!storagePath) {
    throw new Error("URL não pertence ao Firebase Storage.");
  }

  const fileRef = ref(storage, storagePath);
  const bytes = await getBytes(fileRef);
  return new Blob([bytes]);
}

async function fetchBlobFromUrl(url) {
  const response = await fetch(url, {
    method: "GET",
    mode: "cors",
    credentials: "omit",
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.blob();
}

async function downloadDocument(documentItem) {
  const url = String(documentItem?.url || "").trim();
  if (!url) {
    throw new Error("URL do documento não encontrada.");
  }

  let blob;

  try {
    blob = await fetchBlobFromFirebaseStorage(url);
  } catch (firebaseError) {
    console.warn(
      "Download via Firebase Storage indisponível; tentando fetch direto:",
      firebaseError,
    );
    blob = await fetchBlobFromUrl(url);
  }

  if (!(blob instanceof Blob) || blob.size <= 0) {
    throw new Error("Arquivo vazio ou inválido retornado pela origem.");
  }

  const fileName = resolveFileName(documentItem, blob.type);
  const typedBlob =
    blob.type && blob.type !== "application/octet-stream"
      ? blob
      : new Blob([blob], {
          type: blob.type || "application/octet-stream",
        });

  triggerBlobDownload(typedBlob, fileName);
}

function FreightDocumentsModal({
  open,
  onClose,
  loading = false,
  freightData,
  freightLabel,
}) {
  const [downloadingKey, setDownloadingKey] = useState(null);

  const sections = useMemo(
    () => buildFreightDocumentsByStoppingPoint(freightData || {}),
    [freightData],
  );

  const defaultActiveKeys = useMemo(
    () => sections.map((section) => section.id),
    [sections],
  );

  const modalTitle = freightLabel
    ? `Documentos da fatura — ${freightLabel}`
    : "Documentos da fatura";

  const handleDownload = async (documentItem, key) => {
    if (!documentItem?.canDownload || downloadingKey) return;

    setDownloadingKey(key);
    try {
      await downloadDocument(documentItem);
      message.success("Download iniciado.");
    } catch (error) {
      console.error("Erro ao baixar documento:", error);
      message.error(
        "Não foi possível baixar o arquivo. Tente novamente ou use Visualizar.",
      );
    } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <ConfigProvider theme={antdModalTheme}>
      <Modal
        open={open}
        onCancel={onClose}
        title={modalTitle}
        footer={[
          <Button key="close" type="primary" onClick={onClose}>
            Fechar
          </Button>,
        ]}
        width={760}
        destroyOnClose
        styles={{
          body: {
            maxHeight: "70vh",
            overflowY: "auto",
            paddingTop: 8,
          },
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "48px 0" }}>
            <Spin size="large" tip="Carregando documentos..." />
          </div>
        ) : sections.length === 0 ? (
          <Empty description="Nenhum ponto de parada encontrado para este frete." />
        ) : (
          <Collapse
            bordered={false}
            defaultActiveKey={defaultActiveKeys}
            items={sections.map((section) => ({
              key: section.id,
              label: (
                <Space size={8} wrap>
                  <Text strong>{section.title}</Text>
                  <Tag color={section.documents.length > 0 ? "blue" : "default"}>
                    {section.documents.length}{" "}
                    {section.documents.length === 1 ? "arquivo" : "arquivos"}
                  </Tag>
                </Space>
              ),
              children:
                section.documents.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Não há arquivos cadastrados neste ponto de parada."
                  />
                ) : (
                  <List
                    itemLayout="horizontal"
                    dataSource={section.documents}
                    renderItem={(documentItem, index) => {
                      const itemKey = `${section.id}-${index}-${documentItem.url || documentItem.name}`;
                      return (
                        <List.Item
                          actions={[
                            <Tooltip key="view" title="Visualizar documento">
                              <Button
                                type="link"
                                size="small"
                                icon={<EyeOutlined />}
                                disabled={!documentItem.canView}
                                onClick={() => openDocument(documentItem.url)}
                              >
                                Visualizar
                              </Button>
                            </Tooltip>,
                            <Tooltip key="download" title="Baixar documento">
                              <Button
                                type="link"
                                size="small"
                                icon={<DownloadOutlined />}
                                disabled={!documentItem.canDownload}
                                loading={downloadingKey === itemKey}
                                onClick={() =>
                                  handleDownload(documentItem, itemKey)
                                }
                              >
                                Baixar
                              </Button>
                            </Tooltip>,
                          ]}
                        >
                          <List.Item.Meta
                            avatar={
                              <FileTextOutlined
                                style={{
                                  fontSize: 18,
                                  color: theme.colors.primary,
                                }}
                              />
                            }
                            title={documentItem.label}
                            description={documentItem.name}
                          />
                        </List.Item>
                      );
                    }}
                  />
                ),
            }))}
          />
        )}
      </Modal>
    </ConfigProvider>
  );
}

export default FreightDocumentsModal;
